import React, { useState, useEffect } from 'react';
import { FiUser, FiMail, FiPhone, FiMapPin, FiCamera, FiTrash2, FiSave } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';
import './Profile.css';
import Sidebar from '../../components/Sidebar/Sidebar';

const Profile = () => {
    const { t } = useTranslation();
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [updating, setUpdating] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [modalConfig, setModalConfig] = useState({ type: 'success', message: '' });
    const [logoPreview, setLogoPreview] = useState(null);
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        phone: '',
        address: {
            street: '',
            state: '',
            zipCode: '',
            country: 'Turkey'
        }
    });

    useEffect(() => {
        fetchUserProfile();
    }, []);

    const fetchUserProfile = async () => {
        try {
            const token = localStorage.getItem('userToken');
            const response = await fetch('/api/profile/me', {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (response.ok) {
                const data = await response.json();
                setUser(data);
                setFormData({
                    firstName: data.firstName || '',
                    lastName: data.lastName || '',
                    phone: data.phone || '',
                    address: {
                        street: data.address?.street || '',
                        state: data.address?.state || '',
                        zipCode: data.address?.zipCode || '',
                        country: data.address?.country || 'Turkey'
                    }
                });
                if (data.logo) setLogoPreview(data.logo);
            } else {
                setModalConfig({ type: 'error', message: t('profile.loadError') });
                setShowModal(true);
            }
        } catch (error) {
            setModalConfig({ type: 'error', message: t('profile.genericError') });
            setShowModal(true);
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        if (name.startsWith('address.')) {
            const field = name.split('.')[1];
            setFormData(prev => ({ ...prev, address: { ...prev.address, [field]: value } }));
        } else {
            setFormData(prev => ({ ...prev, [name]: value }));
        }
    };

    const handleUpdateProfile = async () => {
        setUpdating(true);
        try {
            const token = localStorage.getItem('userToken');
            const response = await fetch('/api/profile/update', {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(formData)
            });

            const data = await response.json();
            if (response.ok) {
                setUser(data.user);
                setModalConfig({ type: 'success', message: t('profile.updateSuccess') });
            } else {
                setModalConfig({ type: 'error', message: data.msg || t('profile.updateFail') });
            }
            setShowModal(true);
        } catch {
            setModalConfig({ type: 'error', message: t('profile.genericError') });
            setShowModal(true);
        } finally {
            setUpdating(false);
        }
    };

    const handleLogoUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const allowed = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
        if (!allowed.includes(file.type)) {
            setModalConfig({ type: 'error', message: t('profile.invalidImageType') });
            setShowModal(true);
            return;
        }

        if (file.size > 5 * 1024 * 1024) {
            setModalConfig({ type: 'error', message: t('profile.fileSizeLimit') });
            setShowModal(true);
            return;
        }

        const reader = new FileReader();
        reader.onload = async (event) => {
            const base64 = event.target.result;
            setLogoPreview(base64);
            try {
                const token = localStorage.getItem('userToken');
                const res = await fetch('/api/profile/upload-logo', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    },
                    body: JSON.stringify({ logo: base64 })
                });

                const data = await res.json();
                if (res.ok) {
                    setUser(prev => ({ ...prev, logo: data.logoUrl }));
                    setModalConfig({ type: 'success', message: t('profile.logoUploadSuccess') });
                } else {
                    setModalConfig({ type: 'error', message: data.msg || t('profile.logoUploadError') });
                }
                setShowModal(true);
            } catch {
                setModalConfig({ type: 'error', message: t('profile.genericError') });
                setShowModal(true);
            }
        };
        reader.onerror = () => {
            setModalConfig({ type: 'error', message: t('profile.fileReadError') });
            setShowModal(true);
        };
        reader.readAsDataURL(file);
    };

    if (loading) {
        return (
            <div className="profile-page">
                <Sidebar />
                <div className="profile-content">
                    <div className="loading-profile">
                        <div className="loading-spinner-profile"></div>
                        <p>{t('profile.loading')}</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="profile-page">
            <Sidebar />

            {showModal && (
                <div className="modal-overlay" onClick={() => setShowModal(false)}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className={`modal-icon ${modalConfig.type}`}>
                            {modalConfig.type === 'success' ? '✓' : '✕'}
                        </div>
                        <p className="modal-message">{modalConfig.message}</p>
                        <button className="modal-close-btn" onClick={() => setShowModal(false)}>
                            {t('common.ok')}
                        </button>
                    </div>
                </div>
            )}

            <div className="profile-content">
                <div className='profile-page-center'>
                    <div className="profile-header">
                        <h1>{t('profile.settingsTitle')}</h1>
                        <p className="subtitle">{t('profile.settingsSubtitle')}</p>
                    </div>

                    <div className="profile-grid">
                        <div className="profile-card">
                            <h2>{t('profile.companyLogo')}</h2>
                            <div className="logo-upload-area">
                                <div className="logo-preview">
                                    {logoPreview ? (
                                        <img src={logoPreview} alt="Logo" />
                                    ) : (
                                        <div className="logo-placeholder">
                                            <FiCamera size={32} />
                                            <p>{t('profile.uploadLogo')}</p>
                                        </div>
                                    )}
                                </div>
                                <div className="logo-actions">
                                    <label htmlFor="logo-upload" className="upload-btn">
                                        <FiCamera /> {t('common.upload')}
                                    </label>
                                    <input id="logo-upload" type="file" accept="image/*" onChange={handleLogoUpload} style={{ display: 'none' }} />
                                    {logoPreview && (
                                        <button className="delete-logo-btn" onClick={() => setLogoPreview(null)}>
                                            <FiTrash2 /> {t('common.delete')}
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className="profile-card">
                            <h2>{t('profile.personalInfo')}</h2>
                            <div className="form-fields form-fields-grid">
                                <div className="form-group">
                                    <label><FiUser /> {t('profile.firstName')}</label>
                                    <input type="text" name="firstName" value={formData.firstName} onChange={handleInputChange} placeholder={t('profile.placeholder.firstName')} />
                                </div>
                                <div className="form-group">
                                    <label><FiUser /> {t('profile.lastName')}</label>
                                    <input type="text" name="lastName" value={formData.lastName} onChange={handleInputChange} placeholder={t('profile.placeholder.lastName')} />
                                </div>
                                <div className="form-group">
                                    <label><FiMail /> {t('profile.email')}</label>
                                    <input type="email" value={user?.email || ''} disabled className="disabled-input" />
                                </div>
                                <div className="form-group">
                                    <label><FiPhone /> {t('profile.phone')}</label>
                                    <input type="tel" name="phone" value={formData.phone} onChange={handleInputChange} placeholder={t('profile.placeholder.phone')} />
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="profile-right">
                        <div className="profile-card">
                            <h2>{t('profile.addressInfo')}</h2>
                            <div className="form-fields form-fields-grid">
                                <div className="form-group">
                                    <label><FiMapPin /> {t('profile.country')}</label>
                                    <select name="address.country" value={formData.address.country} onChange={handleInputChange}>
                                        {Object.entries(t('countries', { returnObjects: true })).map(([key, value]) => (
                                            <option key={key} value={value}>{value}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label><FiMapPin /> {t('profile.city')}</label>
                                    <input type="text" name="address.state" value={formData.address.state} onChange={handleInputChange} placeholder={t('profile.city')} />
                                </div>
                                <div className="form-group">
                                    <label><FiMapPin /> {t('profile.street')}</label>
                                    <input type="text" name="address.street" value={formData.address.street} onChange={handleInputChange} placeholder={t('profile.placeholder.street')} />
                                </div>
                                <div className="form-group">
                                    <label><FiMapPin /> {t('profile.zipCode')}</label>
                                    <input type="text" name="address.zipCode" value={formData.address.zipCode} onChange={handleInputChange} placeholder={t('profile.placeholder.zipCode')} />
                                </div>
                            </div>
                        </div>

                        <div className="profile-actions">
                            <button className="save-btn" onClick={handleUpdateProfile} disabled={updating}>
                                <FiSave /> {updating ? t('common.saving') : t('common.save')}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Profile;
